import { defineComponent as _defineComponent } from 'vue'
import { computed, ref } from "vue";
import { useTranslations } from "@/hooks/useTranslations";
import type { WellnessCalculationResult } from "@/models/wellness/WellnessCalculationResult";
import {
  getActivityTypeGroupLabel,
  sortCalculationResults,
} from "./activityOrdering";


export default /*#__PURE__*/_defineComponent({
  __name: 'ResultsPanel',
  props: {
    itemName: null,
    calories: null,
    results: null
  },
  setup(__props: any) {

const props = __props as {
  itemName: string;
  calories: number;
  results: WellnessCalculationResult[];
};



const hoveredId = ref<number | null>(null);
const translations = useTranslations();

const orderedResults = computed(() => {
  return sortCalculationResults(props.results);
});

const groupedResults = computed(() => {
  const sections = new Map<
    string,
    {
      title: string;
      results: WellnessCalculationResult[];
    }
  >();

  orderedResults.value.forEach((result) => {
    const title = getActivityTypeGroupLabel(
      result.activityTypeName,
      result.activityName,
    );
    const existingSection = sections.get(title);

    if (existingSection) {
      existingSection.results.push(result);
      return;
    }

    sections.set(title, {
      title,
      results: [result],
    });
  });

  return Array.from(sections.values());
});

const fastest = computed(() => {
  if (orderedResults.value.length === 0) return null;
  return orderedResults.value.reduce(
    (a, b) =>
      (a.minutesToBurn || Number.MAX_SAFE_INTEGER) <
      (b.minutesToBurn || Number.MAX_SAFE_INTEGER)
        ? a
        : b,
    orderedResults.value[0],
  );
});
const maxMinutes = computed(() =>
  orderedResults.value.length > 0
    ? Math.max(...orderedResults.value.map((x) => x.minutesToBurn || 0))
    : 0,
);

const xAxisMax = computed(() => {
  const raw = maxMinutes.value || 1;
  const step = raw <= 60 ? 10 : raw <= 180 ? 20 : 30;
  return Math.ceil(raw / step) * step;
});

const xTicks = computed(() => {
  const ticks = 5;
  const arr: number[] = [];
  for (let i = 0; i <= ticks; i += 1) {
    arr.push(Math.round((xAxisMax.value / ticks) * i));
  }
  return arr;
});

function progressPercent(minutes?: number | null) {
  if (xAxisMax.value === 0 || !minutes) return 0;
  return Math.min((minutes / xAxisMax.value) * 100, 100);
}

function formatNumber(value?: number | null, fractionDigits = 1) {
  if (value == null) {
    return translations.value.get("wellness_not_available");
  }

  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(value);
}

function formatWhole(value?: number | null) {
  if (value == null) {
    return "0";
  }

  return new Intl.NumberFormat("en-US", {
    maximumFractionDigits: 0,
  }).format(value);
}

function formatDuration(minutes?: number | null) {
  const totalMinutes = Math.max(0, Math.round(minutes || 0));

  if (totalMinutes <= 59) {
    return {
      value: formatWhole(totalMinutes),
      lineOne: translations.value.get("wellness_mins_to_burn"),
      lineTwo: translations.value.get("wellness_calories_label"),
    };
  }

  const hours = Math.floor(totalMinutes / 60);
  const remainingMinutes = totalMinutes % 60;
  const formattedHours = formatWhole(hours);

  return {
    value:
      remainingMinutes === 0
        ? `${formattedHours}h`
        : `${formattedHours}h ${remainingMinutes}m`,
    lineOne: translations.value.get("wellness_time_to_burn"),
    lineTwo: translations.value.get("wellness_calories_label"),
  };
}

function circumference(radius: number) {
  return 2 * Math.PI * radius;
}

function dashOffset(value: number, total: number, radius: number) {
  const safeTotal = total === 0 ? 1 : total;
  const pct = Math.min((value / safeTotal) * 100, 100);
  const circ = circumference(radius);
  return circ - (pct / 100) * circ;
}

return { __sfc: true,props, hoveredId, translations, orderedResults, groupedResults, fastest, maxMinutes, xAxisMax, xTicks, progressPercent, formatNumber, formatWhole, formatDuration, circumference, dashOffset }
}

})